/**
 * ConfigService - Centralized configuration management for environment variables
 *
 * This service provides a singleton pattern to access environment variables
 * with proper validation and error handling.
 */
class ConfigService {
  private static instance: ConfigService;
  private config: Record<string, string | undefined>;

  private constructor() {
    // Cache environment variables at initialization
    this.config = {
      API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
      ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT,
      GTAG_MEASUREMENT_ID: process.env.REACT_APP_GTAG_MEASUREMENT_ID,
      GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      ENABLE_GOOGLE_SIGNIN: "true",
      ENABLE_CAPTCHA: process.env.REACT_APP_ENABLE_CAPTCHA,
      RECAPTCHA_SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
      SHOW_SIGNUP_FLOW: process.env.REACT_APP_SHOW_SIGNUP_FLOW,
      INTERCOM_APP_ID: process.env.REACT_APP_INTERCOM_APP_ID,
      SERVER_SORTING_ENABLED: process.env.REACT_APP_SERVER_SORTING_ENABLED,
      SERVER_SEARCH_ENABLED: process.env.REACT_APP_SERVER_SEARCH_ENABLED,
      BUILD_COMMIT: process.env.REACT_APP_BUILD_COMMIT,
      BUILD_TIMESTAMP: process.env.REACT_APP_BUILD_TIMESTAMP,
      POSTHOG_PROJECT_KEY: process.env.REACT_APP_POSTHOG_PROJECT_KEY,
      POSTHOG_HOST: process.env.REACT_APP_POSTHOG_HOST,

      // Add other environment variables as needed
    };

    // Validate required environment variables
    this.validateRequiredConfig();
  }

  /**
   * Get the singleton instance of ConfigService
   */
  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  /**
   * Validate that all required environment variables are present
   */
  private validateRequiredConfig(): void {
    const requiredVars = ["API_BASE_URL"];
    const missingVars = requiredVars.filter(key => !this.config[key]);

    if (missingVars.length > 0) {
      throw new Error(`Missing required environment variables: ${missingVars.map(v => `REACT_APP_${v}`).join(", ")}`);
    }
  }

  /**
   * Get the API base URL
   */
  public getApiBaseUrl(): string {
    const url = this.config.API_BASE_URL;
    if (!url) {
      throw new Error("REACT_APP_API_BASE_URL environment variable is required");
    }

    // Ensure URL starts with https:// if no protocol is specified
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return `https://${url}`;
    }

    return url;
  }

  /**
   * Get the current environment
   */
  public getEnvironment(): string | undefined {
    return this.config.ENVIRONMENT;
  }

  /**
   * Get the auth API base URL (API base + /v2/auth)
   */
  public getAuthApiBaseUrl(): string {
    return `${this.getApiBaseUrl()}/v2/auth`;
  }

  /**
   * Generic method to get any environment variable by key
   * @param key - The environment variable key (without REACT_APP_ prefix)
   */
  public getEnvVar(key: string): string | undefined {
    return this.config[key];
  }

  /**
   * Check if we're in local mode
   */
  public isLocal(): boolean {
    return this.getEnvironment() === "local";
  }

  /**
   * Check if we're in development mode
   */
  public isDevelopment(): boolean {
    return this.getEnvironment() === "dev";
  }

  /**
   * Check if we're in staging mode
   */
  public isStaging(): boolean {
    return this.getEnvironment() === "staging";
  }

  /**
   * Check if we're in production mode
   */
  public isProduction(): boolean {
    return this.getEnvironment() === "prod";
  }

  /**
   * Get the Google Analytics Measurement ID
   */
  public getGtagMeasurementId(): string | undefined {
    return this.config.GTAG_MEASUREMENT_ID;
  }

  /**
   * Get the Google OAuth client ID
   */
  public getGoogleClientId(): string | undefined {
    return this.config.GOOGLE_CLIENT_ID;
  }

  /**
   * Check if Google Sign-In is enabled
   * @returns true only when explicitly set to 'true', false otherwise
   */
  public isGoogleSignInEnabled(): boolean {
    const flag = this.config.ENABLE_GOOGLE_SIGNIN;

    // In prod, require explicit opt-in
    if (this.isProduction()) {
      return flag === "true";
    }

    // Everywhere else (local, dev, staging), enabled.
    return true;
  }

  /**
   * Check if reCAPTCHA is enabled
   */
  public isCaptchaEnabled(): boolean {
    return this.config.ENABLE_CAPTCHA === "true";
  }

  /**
   * Get the reCAPTCHA Site Key
   */
  public getRecaptchaSiteKey(): string | undefined {
    return this.config.RECAPTCHA_SITE_KEY;
  }

  /**
   * Check if signup flow should be shown
   * @returns true if signup should be shown (default true if not set), false otherwise
   */
  public shouldShowSignupFlow(): boolean {
    // Default to true if not set, only return false if explicitly set to 'false'
    return this.config.SHOW_SIGNUP_FLOW !== "false";
  }

  /**
   * Get the Intercom App ID
   */
  public getIntercomAppId(): string {
    return this.config.INTERCOM_APP_ID?.trim() || "";
  }

  /**
   * Feature flag: enable server-side sorting for users list
   */
  public isServerSortingEnabled(): boolean {
    return this.config.SERVER_SORTING_ENABLED === "true";
  }

  /**
   * Feature flag: enable server-side search for users list
   */
  public isServerSearchEnabled(): boolean {
    return this.config.SERVER_SEARCH_ENABLED === "true";
  }

  /**
   * Get build commit SHA
   */
  public getBuildCommit(): string | undefined {
    return this.config.BUILD_COMMIT;
  }

  /**
   * Get build timestamp
   */
  public getBuildTimestamp(): string | undefined {
    return this.config.BUILD_TIMESTAMP;
  }

  /**
   * Get build info as an object
   */
  public getBuildInfo(): { env: string; commit: string; built: string } {
    return {
      env: this.getEnvironment() || "local",
      commit: this.getBuildCommit()?.substring(0, 7) || "latest",
      built: this.getBuildTimestamp() || new Date().toISOString(),
    };
  }

  /**
   * Get the PostHog project key (also called project API key / public key).
   * Returns undefined when unset so callers can no-op gracefully in local/dev.
   */
  public getPostHogProjectKey(): string | undefined {
    return this.config.POSTHOG_PROJECT_KEY;
  }

  /**
   * Get the PostHog API host. Defaults to the US cloud when unset.
   */
  public getPostHogHost(): string | undefined {
    return this.config.POSTHOG_HOST;
  }

  /**
   * Log build info to console
   */
  public logBuildInfo(): void {
    const info = this.getBuildInfo();
    console.info(`[RunPlutus] env=${info.env} commit=${info.commit} built=${info.built}`);
  }
}

// Export a singleton instance
export const configService = ConfigService.getInstance();
export default configService;
